// src/pages/ProvidersPage.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getMyProviders, addProvider } from "../lib/providerApi";
import { apiGetFriends } from "../lib/friendsApi";
import {
  getMyLinkedShares,
  createLinkedShare,
  revokeLinkedShare,
} from "../lib/linkedShareApi";
import "./ProvidersPage.css";

const ALL_PACKAGES = [
  {
    id: "free",
    label: "Free",
    subtitle: "Basispakket",
    description:
      "Je kunt sessies starten en joinen die alleen het Free pakket nodig hebben.",
  },
  {
    id: "sports",
    label: "Sports",
    subtitle: "Uitgebreid sportpakket",
    description:
      "Je kunt sessies starten en joinen die Free óf Sports nodig hebben.",
  },
  {
    id: "all_access",
    label: "All access",
    subtitle: "Volledige toegang",
    description:
      "Je kunt alle sessies starten en joinen, ongeacht welk pakket ze nodig hebben.",
  },
];

// Testpakketten hiërarchie
const PACKAGE_LEVELS = {
  free: 0,
  sports: 1,
  all_access: 2,
};

function normalizeId(id) {
  return String(id || "").toLowerCase();
}

function getPackageMeta(providerId) {
  const norm = normalizeId(providerId);
  return ALL_PACKAGES.find((p) => p.id === norm) || null;
}

function getHighestTestPackage(providers) {
  let best = null;
  let bestLevel = -1;

  for (const p of providers || []) {
    const meta = getPackageMeta(p.providerId);
    if (!meta) continue;
    const level = PACKAGE_LEVELS[meta.id] ?? -1;
    if (level > bestLevel) {
      bestLevel = level;
      best = meta;
    }
  }

  if (!best) return null;
  return {
    meta: best,
    level: bestLevel,
  };
}

function ProvidersPage({ currentUser, token }) {


    useEffect(() => {
    document.body.classList.add("providers-body-lock");
    return () => document.body.classList.remove("providers-body-lock");
  }, []);

  const safeToken = token || null;
  const user = currentUser || null;

  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  // Linked sharing state
  const [friends, setFriends] = useState([]);
  const [sharesOwned, setSharesOwned] = useState([]);
  const [sharesReceived, setSharesReceived] = useState([]);

  const [sharePkg, setSharePkg] = useState(""); // "sports" | "all_access"
  const [shareOpen, setShareOpen] = useState(false);

  const [selectedFriendIds, setSelectedFriendIds] = useState(() => new Set());
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareInfo, setShareInfo] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!safeToken) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const list = await getMyProviders(safeToken);
        if (!cancelled) {
          setProviders(list || []);
        }
      } catch (e) {
        console.error("getMyProviders error", e);
        if (!cancelled) {
          setError("Kon je pakketten niet laden.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [safeToken]);

const hasPackage = useCallback(
  (id) =>
    providers.some(
      (p) => normalizeId(p.providerId) === normalizeId(id)
    ),
  [providers]
);


  async function handleAddPackage(id) {
    if (busyId || !safeToken) return;
    setBusyId(id);
    setError("");

    try {
      const added = await addProvider(id, safeToken);
      setProviders((prev) => {
        const exists = prev.find(
          (p) => normalizeId(p.providerId) === normalizeId(added.providerId)
        );
        if (exists) return prev;
        return [...prev, added];
      });
    } catch (e) {
      console.error("addProvider error", e);
      setError("Koppelen mislukt. Probeer het later opnieuw.");
    } finally {
      setBusyId(null);
    }
  }

 const highestTestPackage = useMemo(() => {
  const own = providers || [];

  // ontvangen shares tellen mee als toegang
  const receivedAsProviders = (sharesReceived || []).map((s) => ({
    providerId: s.providerId,
  }));

  return getHighestTestPackage([...own, ...receivedAsProviders]);
}, [providers, sharesReceived]);


  // --- Linked share helpers ---
  const ownedForPkg = useMemo(() => {
    const pid = normalizeId(sharePkg);
    return (sharesOwned || []).filter(
      (s) => normalizeId(s.providerId) === pid
    );
  }, [sharesOwned, sharePkg]);

  const ownedGuestIdSetForPkg = useMemo(() => {
    const set = new Set();
    for (const s of ownedForPkg) {
      const gid = s?.guest?.id;
      if (gid != null) set.add(String(gid));
    }
    return set;
  }, [ownedForPkg]);

const canManagePkg = useMemo(() => {
  const pid = normalizeId(sharePkg);
  if (!pid) return false;
  return hasPackage(pid);
}, [sharePkg, hasPackage]);


  async function loadLinkedData() {
    if (!safeToken) return;

    setShareError("");
    setShareInfo("");
    try {
      const [f, ls] = await Promise.all([
        apiGetFriends(safeToken),
        getMyLinkedShares(safeToken),
      ]);
      setFriends(Array.isArray(f) ? f : []);
      setSharesOwned(ls?.owned || []);
      setSharesReceived(ls?.received || []);
    } catch (e) {
      console.error("[ProvidersPage] loadLinkedData error", e);
      setShareError("Kon je vrienden / gekoppelde vrienden niet laden.");
    }
  }

  useEffect(() => {
    // laad linked sharing data zodra je ingelogd bent
    if (!safeToken) return;
    loadLinkedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeToken]);

  function openShareForPackage(pkgId) {
    const pid = normalizeId(pkgId);

    // alleen sports / all_access mogen
    if (!["sports", "all_access"].includes(pid)) return;

    // alleen open als gebruiker dit pakket heeft
    if (!hasPackage(pid)) return;

    setSharePkg(pid);
    setShareOpen(true);
    setShareBusy(false);
    setShareError("");
    setShareInfo("");

    // reset selectie; handig bij heropenen
    setSelectedFriendIds(new Set());
  }

  function closeShare() {
    setShareOpen(false);
    setSharePkg("");
    setSelectedFriendIds(new Set());
    setShareBusy(false);
    setShareError("");
    setShareInfo("");
  }

  function toggleSelectFriend(friendId) {
    const id = String(friendId);
    setSelectedFriendIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleShareSelected() {
    if (!safeToken) return;
    const pid = normalizeId(sharePkg);

    if (!["sports", "all_access"].includes(pid)) {
      setShareError("Dit pakket kan niet gekoppeld worden.");
      return;
    }

    if (!hasPackage(pid)) {
      setShareError("Je kunt alleen vrienden koppelen aan een pakket dat je hebt.");
      return;
    }

    const friendIds = [...selectedFriendIds];
    if (friendIds.length === 0) {
      setShareError("Selecteer minimaal 1 vriend.");
      return;
    }

    setShareBusy(true);
    setShareError("");
    setShareInfo("");

    try {
      // server checks ook OWNER_NO_ACCESS, en friend-check doen we client-side via friends lijst
      // (we sturen alleen ids die al in friends zitten)
      const friendIdSet = new Set((friends || []).map((f) => String(f.id)));
      const valid = friendIds.filter((id) => friendIdSet.has(String(id)));

      if (valid.length === 0) {
        setShareError("Je selectie bevat geen geldige vrienden.");
        setShareBusy(false);
        return;
      }

      // Create shares (upsert gedrag in backend)
      await Promise.all(
        valid.map((gid) => createLinkedShare(gid, pid, safeToken))
      );

      setShareInfo("Vrienden gekoppeld.");
      await loadLinkedData();
      setSelectedFriendIds(new Set());
    } catch (e) {
      console.error("[linked-share] create error", e);
      const code = e?.code || e?.message;

      if (code === "OWNER_NO_ACCESS") {
        setShareError("Je hebt zelf geen toegang tot dit pakket.");
      } else if (code === "INVALID_GUEST") {
        setShareError("Ongeldige vriend geselecteerd.");
      } else if (code === "GUEST_NOT_FOUND") {
        setShareError("Deze gebruiker bestaat niet (meer).");
      } else if (code === "UNAUTHENTICATED") {
        setShareError("Je bent niet ingelogd.");
      } else {
        setShareError("Koppelen mislukt. Probeer het later opnieuw.");
      }
    } finally {
      setShareBusy(false);
    }
  }

  async function handleRevokeShare(shareId) {
    if (!safeToken) return;
    if (!shareId) return;

    setShareBusy(true);
    setShareError("");
    setShareInfo("");

    try {
      await revokeLinkedShare(shareId, safeToken);
      setShareInfo("Koppeling verwijderd.");
      await loadLinkedData();
    } catch (e) {
      console.error("[linked-share] revoke error", e);
      const code = e?.code || e?.message;
      if (code === "NOT_FOUND") setShareError("Koppeling niet gevonden.");
      else if (code === "UNAUTHENTICATED") setShareError("Je bent niet ingelogd.");
      else setShareError("Verwijderen mislukt. Probeer het later opnieuw.");
    } finally {
      setShareBusy(false);
    }
  }

  // Geen token → simpele state
  if (!safeToken) {
    return (
      <div className="providers-page">
        <div className="providers-inner">
          <h1 className="providers-title">Mijn pakketten</h1>
          <p className="providers-intro">Log eerst in om pakketten te kunnen koppelen.</p>
        </div>
      </div>
    );
  }

  const sharePkgMeta = getPackageMeta(sharePkg);

  return (
    <div className="providers-page">
      <div className="providers-inner">
        {/* Titel + intro */}
        <h1 className="providers-title">Mijn pakketten</h1>

        <p className="providers-intro">
          Hier geef je aan welke pakketten / abonnementen jij hebt bij je sport-/TV-provider.
          Bond gebruikt dit later om te bepalen aan welke sessies je mag deelnemen. In deze fase
          werken we met testpakketten{" "}
          <span className="providers-intro-accent">Free</span>,{" "}
          <span className="providers-intro-accent">Sports</span>, en{" "}
          <span className="providers-intro-accent">All access</span>.
        </p>

        {user && (
          <p className="providers-user">
            Ingelogd als <span className="providers-user-name">{user.name}</span>{" "}
            <span className="providers-user-email">({user.email})</span>
          </p>
        )}

        {/* Huidig toegang-banner */}
        <section className="providers-access">
          <div className="providers-access-card">
            <div className="providers-access-header">Huidige toegang</div>
            {highestTestPackage ? (
              <div className="providers-access-body">
                <span>
                  Je hoogste pakket is{" "}
                  <span className="providers-access-strong">
                    {highestTestPackage.meta.label}
                  </span>
                  . Alle sessies met een lager pakket kun je joinen.
                </span>
              </div>
            ) : (
              <div className="providers-access-body">
                <span>
                  Je hebt nog geen testpakket gekoppeld. Je kunt alleen sessies joinen die geen
                  pakket vereisen.
                </span>
              </div>
            )}
            {highestTestPackage && (
              <div className="providers-access-pill">
                <span className="providers-access-pill-dot" />
                Actief pakket
              </div>
            )}
          </div>
        </section>

        {error && <div className="providers-error-banner">{error}</div>}

        {loading ? (
          <p className="providers-loading">Laden…</p>
        ) : (
          <>
            {/* Gekoppelde pakketten */}
            <section className="providers-section">
              <h2 className="providers-section-title">Gekoppelde pakketten</h2>

              {providers.length === 0 ? (
                <p className="providers-muted">Je hebt nog geen pakketten gekoppeld.</p>
              ) : (
                <ul className="providers-linked-list">
                  {providers.map((p) => {
                    const meta = getPackageMeta(p.providerId);
                    const label = meta?.label || p.providerId;
                    const subtitle = meta?.subtitle || "Gekoppeld pakket";

                    const pid = normalizeId(p.providerId);
                    const canLinkFriends = ["sports", "all_access"].includes(pid);

                    return (
                      <li key={p.id} className="providers-linked-item">
                        <div className="providers-linked-main">
                          <div className="providers-linked-label">{label}</div>
                          <div className="providers-linked-subtitle">{subtitle}</div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="providers-linked-badge">gekoppeld</span>

                          {/* ✅ Alleen tonen als user dit pakket heeft (dat is hier zo) én alleen bij sports/all_access */}
                          {canLinkFriends && (
                            <button
                              type="button"
                              className="providers-linkedshare-btn"
                              onClick={() => openShareForPackage(pid)}
                              title="Koppel vrienden aan je pakket"
                            >
                              Koppel vrienden
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>



            {/* Ontvangen pakketten (van vrienden) */}
<section className="providers-section">
  <h2 className="providers-section-title">Pakketten die jij ontvangt</h2>
  <p className="providers-section-text">
    Dit zijn pakketten die een vriend met jou heeft gekoppeld. Ontvangen pakketten
    kun je niet door-koppelen.
  </p>

  {sharesReceived.length === 0 ? (
    <p className="providers-muted">
      Je ontvangt nog geen gekoppelde pakketten van vrienden.
    </p>
  ) : (
    <ul className="providers-linked-list">
      {sharesReceived.map((s) => {
        const meta = getPackageMeta(s.providerId);
        const label = meta?.label || s.providerId;

        const ownerName = s.owner?.name || "Onbekende gebruiker";
        const ownerEmail = s.owner?.email || "";

        return (
          <li key={s.id} className="providers-linked-item">
            <div className="providers-linked-main">
              <div className="providers-linked-label">{label}</div>
              <div className="providers-linked-subtitle">
  Gekoppeld abonnement met{" "}
  <strong>{ownerName}</strong>
  {ownerEmail ? ` (${ownerEmail})` : ""}
</div>

            </div>

            <span className="providers-linked-badge">ontvangen</span>
          </li>
        );
      })}
    </ul>
  )}
</section>




            {/* Pakketten koppelen */}
            <section className="providers-section">
              <h2 className="providers-section-title">Pakketten koppelen</h2>
              <p className="providers-section-text">
                Dit is stap 1: aangeven welke pakketten jij hebt. De daadwerkelijke toegang tot
                wedstrijden blijft altijd via de officiële app/website van je provider. Bond gebruikt
                deze info alleen om te bepalen welke sessies je mag starten of joinen.
              </p>

              <div className="providers-grid">
                {ALL_PACKAGES.map((pkg) => {
                  const active = hasPackage(pkg.id);
                  const isBusy = busyId === pkg.id;

                  const cardClasses = [
                    "providers-package-card",
                    active && "providers-package-card--active",
                    isBusy && "providers-package-card--busy",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      disabled={active || isBusy}
                      onClick={() => handleAddPackage(pkg.id)}
                      className={cardClasses}
                    >
                      <div className="providers-package-header">
                        <div className="providers-package-title">{pkg.label}</div>
                        {active && (
                          <div className="providers-package-status">✓ Gekoppeld</div>
                        )}
                      </div>
                      <div className="providers-package-subtitle">{pkg.subtitle}</div>
                      <p className="providers-package-description">
                        {active
                          ? "Dit pakket staat al gekoppeld aan je Bondaccount."
                          : pkg.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ===========================
                LINKED SHARING MODAL
               =========================== */}
            {shareOpen && (
              <div className="providers-modal-backdrop" onMouseDown={closeShare}>
                <div
                  className="providers-modal"
                  onMouseDown={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                >
                  <div className="providers-modal-header">
                    <div className="providers-modal-title">
                      Koppel vrienden aan je pakket{" "}
                      <span className="providers-modal-title-accent">
                        {sharePkgMeta?.label || sharePkg}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="providers-modal-close"
                      onClick={closeShare}
                      aria-label="Sluiten"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="providers-modal-body">
                    <p className="providers-section-text" style={{ marginBottom: 10 }}>
                      Alleen vrienden die je al hebt toegevoegd in Bond kun je koppelen. Vrienden die
                      gekoppeld zijn kunnen sessies joinen die dit pakket vereisen, ook als ze het
                      zelf niet hebben.
                    </p>

                    {!canManagePkg && (
                      <div className="providers-error-banner" style={{ marginBottom: 10 }}>
                        Je kunt alleen vrienden koppelen aan een pakket dat je hebt.
                      </div>
                    )}

                    {shareError && (
                      <div className="providers-error-banner" style={{ marginBottom: 10 }}>
                        {shareError}
                      </div>
                    )}

                    {shareInfo && (
                      <div
                        className="providers-info-banner"
                        style={{
                          marginBottom: 10,
                          fontSize: 12,
                          color: "#bbf7d0",
                          background: "rgba(6,95,70,0.22)",
                          border: "1px solid rgba(34,197,94,0.6)",
                          borderRadius: 12,
                          padding: "8px 10px",
                        }}
                      >
                        {shareInfo}
                      </div>
                    )}

                    {/* Vrienden kiezen */}
                    <div className="providers-linkedshare-grid">
                      {(friends || []).length === 0 ? (
                        <div className="providers-muted">
                          Je hebt nog geen vrienden in Bond. Voeg eerst vrienden toe.
                        </div>
                      ) : (
                        <ul className="providers-linkedshare-list">
                          {friends.map((f) => {
                            const fid = String(f.id);
                            const already = ownedGuestIdSetForPkg.has(fid);
                            const checked = selectedFriendIds.has(fid);

                            return (
                              <li key={fid} className="providers-linkedshare-item">
                                <label className="providers-linkedshare-label">
                                  <input
                                    type="checkbox"
                                    disabled={already || shareBusy || !canManagePkg}
                                    checked={checked}
                                    onChange={() => toggleSelectFriend(fid)}
                                  />
                                  <span className="providers-linkedshare-name">
                                    {f.name || "Onbekend"}
                                  </span>
                                  <span className="providers-linkedshare-email">
                                    {f.email ? `(${f.email})` : ""}
                                  </span>
                                </label>

                                {already && (
                                  <span className="providers-linked-badge">
                                    al gekoppeld
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    <div className="providers-modal-actions">
                      <button
                        type="button"
                        className="providers-secondary-btn"
                        onClick={closeShare}
                        disabled={shareBusy}
                      >
                        Sluiten
                      </button>

                      <button
                        type="button"
                        className="providers-primary-btn"
                        onClick={handleShareSelected}
                        disabled={
                          shareBusy ||
                          !canManagePkg ||
                          selectedFriendIds.size === 0
                        }
                      >
                        {shareBusy ? "Bezig…" : "Koppel geselecteerde vrienden"}
                      </button>
                    </div>

                    {/* Overzicht huidige koppelingen (owned) */}
                    <div style={{ marginTop: 14 }}>
                      <div
                        className="providers-section-title"
                        style={{ fontSize: 13, marginBottom: 8 }}
                      >
                        Huidige koppelingen voor {sharePkgMeta?.label || sharePkg}
                      </div>

                      {ownedForPkg.length === 0 ? (
                        <div className="providers-muted">
                          Je hebt nog geen vrienden gekoppeld aan dit pakket.
                        </div>
                      ) : (
                        <ul className="providers-linked-list" style={{ maxWidth: "100%" }}>
                          {ownedForPkg.map((s) => {
                            const sid = s.id;
                            const g = s.guest;

                            return (
                              <li key={sid} className="providers-linked-item">
                                <div className="providers-linked-main">
                                  <div className="providers-linked-label">
                                    {g?.name || "Onbekend"}
                                  </div>
                                  <div className="providers-linked-subtitle">
                                    {g?.email || ""}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="providers-danger-btn"
                                  onClick={() => handleRevokeShare(sid)}
                                  disabled={shareBusy}
                                  title="Koppeling verwijderen"
                                >
                                  Verwijder
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    {/* Overzicht ontvangen shares (optioneel, informatief) */}
                    <div style={{ marginTop: 14 }}>
                      <div
                        className="providers-section-title"
                        style={{ fontSize: 13, marginBottom: 8 }}
                      >
                        Pakketten die jij ontvangt
                      </div>

                      {(sharesReceived || []).length === 0 ? (
                        <div className="providers-muted">
                          Je ontvangt nog geen gekoppelde pakketten van vrienden.
                        </div>
                      ) : (
                        <ul className="providers-linked-list" style={{ maxWidth: "100%" }}>
                          {sharesReceived.map((s) => {
                            const sid = s.id;
                            const owner = s.owner;
                            const meta = getPackageMeta(s.providerId);
                            const label = meta?.label || s.providerId;

                            return (
                              <li key={sid} className="providers-linked-item">
                                <div className="providers-linked-main">
                                  <div className="providers-linked-label">
                                    {label}
                                  </div>
                                  <div className="providers-linked-subtitle">
                                    van {owner?.name || "Onbekend"}{" "}
                                    {owner?.email ? `(${owner.email})` : ""}
                                  </div>
                                </div>

                                <span className="providers-linked-badge">ontvangen</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProvidersPage;
